import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DialogCategoriesComponent } from 'src/app/common/dialogs/dialog-categories/dialog-categories.component';
import { DialogConfirmComponent } from 'src/app/common/dialogs/dialog-confirm/dialog-confirm.component';
import { DialogDeleteComponent } from 'src/app/common/dialogs/dialog-delete/dialog-delete.component';
import { report, reportCategory } from 'src/app/dashboards/models';
import { getWidgetById, State } from 'src/app/dashboards/state';
import { gridItem } from 'src/app/models';
import { DialogSaveAsEditData, DialogSaveAsEditTypes, ReportsDialogSaveAsEditComponent } from 'src/app/reports/reports-details/nested-components/reports-dialog-save-as-edit/reports-dialog-save-as-edit.component';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import { enumToObjects } from '../../helpers/helpers';
import { ReportsDialogSubscriptionInfoComponent } from '../reports-dialog-subscription-info/reports-dialog-subscription-info.component';

@Component({
    selector: 'app-reports-list',
    templateUrl: './reports-list.component.html',
    styleUrls: ['./reports-list.component.scss'],
    standalone: false
})
export class ReportsListComponent implements OnInit, OnDestroy, AfterViewInit  {
  categoriesOpened: boolean = false;
  filter: string = "";
  customReports: boolean = false;
  categoryId: number = -1;
  reports: report[];
  reportsFiltered: report[];
  reportCategories: Array<reportCategory>;
  order: { by: string, desc: boolean }
  reportId: number = null;
  subReportId: number = null;
  isReportSelected:boolean = false;
  canEdit:boolean=false;
  subscriptionInfoActive:boolean=false;
  reportUnsaved: any;
  isReportExpanded: boolean = false;
  getFromDashboard: Subject<gridItem | boolean> = new Subject<gridItem | boolean>();
  reportSources: Array<any>;
  reportSourcesSelectedIndex: number;
  selectedReportSource: reportSources;
  isMultiSource: boolean = false;
  @ViewChildren('reportItem') reportItems;
  filteredReportsDefined: Subject<void> = new Subject<void>();
  private combinedSubscription: Subscription;

  constructor(private DashboardsService: DashboardService, private i18nextPipe: I18NextPipe, public dialog: MatDialog, private store: Store<State>,
              private activatedRoute: ActivatedRoute, private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.getReports();
    this.DashboardsService.getReportsCanEdit().subscribe(value => this.canEdit = value);
    this.order = { by: "", desc: false };

    let reportTypesObjects: any[] = enumToObjects(reportSources);
    reportTypesObjects.forEach(obj => {
      obj.text = this.i18nextPipe.transform('dashboard:' + obj.value);
      obj.key = obj.key * 1;
    });
    this.reportSources = reportTypesObjects;
    this.reportSourcesSelectedIndex = reportSources.AllSources;
  }

  ngAfterViewInit() {
    this.route.queryParams.subscribe(params => {
      let id = params.widgetId;
      if (id !== undefined) {
        let gridItem$ = this.store.select(getWidgetById({ id })).pipe(take(1)) as Observable<gridItem>;
        let filteredReportsDefined$ = this.filteredReportsDefined.asObservable();

        // combine observables and wait for both to emit value - source: https://stackoverflow.com/questions/44004144/how-to-wait-for-two-observables-in-rxjs
        // so, it will execute subscription handler when reportsFiltered is defined and gridItem$ returns a gridItem
        this.combinedSubscription = combineLatest(gridItem$, filteredReportsDefined$, (gi, f) => gi)
          .subscribe(gi => {
            if (gi) {
              let gridItem: gridItem = JSON.parse(JSON.stringify(gi));
              //this.isReportSelected = true;
              //this.reportId = gridItem.setup.reportId;
              //this.subReportId = 0;

              let report: report = this.reportsFiltered.find(rep => rep.id == gridItem.setup.reportId);
              this.select(report);

              setTimeout(() => {
                this.getFromDashboard.next(gridItem);
              }, 0);
            }
          });
      }
    });
  }

  ngOnDestroy(): void {
    this.combinedSubscription?.unsubscribe();
  }

  setSelectedReportOnFocus(){
    this.reportItems.find(i => i.nativeElement.id == `reportIds-${this.reportId}-${this.subReportId}`)?.nativeElement.children[0].focus();
  }

  getReports() {
    let reportsApi = this.DashboardsService.getAllReports();
    reportsApi.pipe(take(1)).subscribe(reports => {
      this.reports = reports;
      this.isMultiSource = reports.map(r => r.report_Source).filter((value, index, self) => self.indexOf(value) === index).length > 1;
      this.filterReports();
    });

    let allCategories: reportCategory = { id: -1, category:  this.i18nextPipe.transform('dashboard:All Categories') , inUse: 1 };
    this.DashboardsService.getReportCategories().subscribe(rc => this.reportCategories = [...[allCategories],...rc]);
  }

  filterReports() {
    this.reportsFiltered = this.reports.filter(r => {
      let title = (r.isNonStandardReport ? "[special] " + r.shortDescription : r.shortDescription).toLowerCase();
      return (this.filter == "" ? true : (title.includes(this.filter.toLowerCase()))) &&
        (r.isCustom == this.customReports) &&
        ((this.categoryId == -1 || r.subReportId == 0) ? true : this.categoryId == r.categoryId) &&
        (this.reportSourcesSelectedIndex == reportSources.AllSources || r.report_Source == this.reportSourcesSelectedIndex)
    });

    this.filteredReportsDefined.next();
  }

  sort(by: string) {
    this.order = { by: by, desc: this.order.by == by ? !this.order.desc : false }
    this.reportsFiltered.sort((a, b) => a[by].localeCompare(b[by], undefined, { sensitivity: 'base' }) * (this.order.desc ? -1 : 1));
  }

  editReport(report: report) {
    let dialogRef = this.dialog.open(ReportsDialogSaveAsEditComponent, {
      data: new DialogSaveAsEditData({ ...report }, DialogSaveAsEditTypes.Edit, null),
      panelClass: 'dialog-standard-pannel',
      backdropClass: "backdrop-invisible",
      width: "440px",
      height: "410px"
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.getReports()
      };
    });
  }

  deleteReport(report: report) {
    const dialogRef = this.dialog.open(DialogDeleteComponent, {
      data: {
        component: this.i18nextPipe.transform('dashboard:Report'),
        additionalText: this.i18nextPipe.transform('dashboard:Report Name') + ": " + report.shortDescription
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.DashboardsService.deleteReport(report.subReportId).subscribe(() => {
          this.getReports();
        })
      }
    });
  }

  showCategories() {
    const allCategories: reportCategory = { id: -1, category: this.i18nextPipe.transform('dashboard:All Categories') , inUse: 1 };
    const dialogRef = this.dialog.open(DialogCategoriesComponent, {
      panelClass: 'dialog-standard-pannel',
      backdropClass: "backdrop-invisible",
      data: { categories: this.reportCategories.filter(c => c.id !== -1) },
      width: "495px",
      height: "480px"
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        this.reportCategories = [...[allCategories], ...JSON.parse(JSON.stringify(data.categories))];
      }
    });
  }

  select(report: report) {
    if (report.isNonStandardReport) {
      const params = new HttpParams()
        .set('path', report.reportPath)
        .set('reportId', report.id.toString())
        .set('title', report.shortDescription)
        .set('subReportId', report.subReportId.toString())
        .set('fromList', 'true');
      window.open("/admin/StatisticReports/ReportViewerContainer?" + params.toString(), "_blank");
      return;
    }

    let selectingReport = (report: report) => {
      this.isReportSelected = true;
      this.reportId = report.id;
      this.subReportId = report.subReportId;
      this.selectedReportSource = report.report_Source;
      this.getFromDashboard.next(false);
    };

    if (this.isReportSelected &&  this.reportUnsaved ) {
      let dialogRef = this.dialog.open(DialogConfirmComponent, {
        disableClose: true,
        data: {
          message: this.i18nextPipe.transform('There are unsaved config changes in the report you are about to leave.'),
          subMessage: this.i18nextPipe.transform('Do you want to leave without saving it?')
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          selectingReport(report);
        }
      });
    } else {
      selectingReport(report);
    }
  }

  showSubscriptionInfo(){
    this.subscriptionInfoActive=true;

    let dialogRef = this.dialog.open(ReportsDialogSubscriptionInfoComponent, {
      data: [],
      width: "508px",
      height: "auto",
      panelClass: 'dialog-standard-pannel'
    });

    dialogRef.afterClosed().subscribe(result => {
      this.subscriptionInfoActive=false;
    });
  }

  reportModified(e){
    this.reportUnsaved = e;
  }

  reportClose($event){
    this.isReportSelected = false;
    this.isReportExpanded = false;
    this.reportId = null;
    this.subReportId = null;
  }

  reportExpanded($event){
    this.isReportExpanded = $event;
  }

  isDetailsComponentShown(expectedSource: reportSources) {
    return this.isReportSelected && expectedSource == this.selectedReportSource;
  }
}

export enum reportSources {
  AllSources,
  PCC,
  WebEngage
}
